/**
 * Demo analytics tracker — canonical source (demo/tracker/tracker.ts).
 *
 * Vendored into each app as `frontend/src/lib/tracker.ts` by
 * scripts/sync-tracker.mjs — EDIT THE CANONICAL COPY and re-run the sync.
 *
 * Brand-pure by construction: this file contains ZERO brand/product literals
 * and beacons only to the RELATIVE endpoint `/__t` (the host nginx proxies it
 * server-side to the analytics ingest, so no third-party hostname ever appears
 * in client traffic or bundles). The only config is the generated site id
 * (`brand.analyticsSite`, from gen-brand-ts.mjs).
 *
 * What it records (names must match the ingest allowlist):
 *   page_view      — on init + SPA route changes (history patch + popstate)
 *   page_leave     — {seconds, max_scroll} once per page instance, on route
 *                    change / tab-hide / pagehide
 *   ui_click       — {tag, text<=80, href?, testid?, role?} delegated capture
 *   scroll_depth   — {depth} milestone crossings (25/50/75/100)
 *   demo_lock_view — {feature, sample_url?} from the 'demo-lock' CustomEvent
 *                    every app already dispatches at its locked AI steps
 *   heartbeat      — {} every 15s while the tab is visible
 *
 * Attribution: a `?dl=<token>` query param (minted demo link) is persisted to
 * localStorage and rides on every batch; UTM params are captured first-touch.
 *
 * Privacy: no cookies, no fingerprinting; session id is a per-tab
 * sessionStorage UUID; DNT no-ops the whole tracker; kill switch via
 * localStorage["__t_off"] = "1". All delivery failures are silent.
 */

// Both Vite and Next statically replace `process.env.NODE_ENV` at build time;
// this declaration only satisfies tsc in apps without node types (erased at compile).
declare const process: { env: { NODE_ENV?: string } }

interface TrackerConfig {
  site: string
  endpoint?: string
}

interface QueuedEvent {
  name: string
  path: string
  props: Record<string, unknown>
  ts: string
  referrer: string | null
}

const SESSION_KEY = '__t_sid'
const LINK_KEY = '__t_dl'
const UTM_KEY = '__t_utm'
const OFF_KEY = '__t_off'

const BATCH_MAX = 10
const MAX_BATCH_SEND = 25
const IDLE_MS = 5000
const HEARTBEAT_MS = 15000
const CLICK_THROTTLE_MS = 300
const SCROLL_MILESTONES = [25, 50, 75, 100]

let installed = false

export function initTracker(cfg: TrackerConfig): void {
  try {
    if (installed || typeof window === 'undefined') return
    // Prod-build only: statically replaced by both Vite and Next.
    if (process.env.NODE_ENV !== 'production') return
    if (navigator.doNotTrack === '1') return
    if (safeGet(localStorage, OFF_KEY) === '1') return
    if (!cfg || !cfg.site) return
    installed = true
    start(cfg.site, cfg.endpoint || '/__t')
  } catch {
    /* analytics must never break a demo */
  }
}

/* ------------------------------------------------------------------ core */

function start(site: string, endpoint: string): void {
  const sessionId = getSessionId()

  // ?dl=<token> — persist so return visits (and new tabs) stay attributed.
  try {
    const dl = new URLSearchParams(location.search).get('dl')
    if (dl && /^[A-Za-z0-9_-]{8,32}$/.test(dl)) localStorage.setItem(LINK_KEY, dl)
  } catch {
    /* ignore */
  }
  const linkToken = safeGet(localStorage, LINK_KEY)

  // First-touch UTM (describes the link, never the person).
  captureUtm()

  let queue: QueuedEvent[] = []
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let firstPageView = true

  // --- per-page-instance state ---
  let currentPath = location.pathname
  let pageStart = Date.now()
  let maxScroll = 0
  let milestonesSent: number[] = []
  let leaveSent = false
  let lastClickAt = 0

  function enqueue(name: string, props: Record<string, unknown> = {}, path?: string): void {
    queue.push({
      name,
      path: (path ?? currentPath).slice(0, 400),
      props,
      ts: new Date().toISOString(),
      referrer: name === 'page_view' ? document.referrer || null : null,
    })
    if (queue.length >= BATCH_MAX) flush()
    else scheduleIdleFlush()
  }

  function scheduleIdleFlush(): void {
    if (idleTimer !== null) clearTimeout(idleTimer)
    idleTimer = setTimeout(flush, IDLE_MS)
  }

  function send(events: QueuedEvent[]): void {
    const payload = JSON.stringify({
      session_id: sessionId,
      site,
      link_token: linkToken,
      events,
    })
    try {
      if ('sendBeacon' in navigator) {
        // Blob with an explicit JSON type — a bare string arrives as text/plain
        // and the ingest 415s it.
        const ok = navigator.sendBeacon(endpoint, new Blob([payload], { type: 'application/json' }))
        if (ok) return
      }
      void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => undefined)
    } catch {
      /* silent */
    }
  }

  function flush(): void {
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
    if (queue.length === 0) return
    const pending = queue
    queue = []
    for (let i = 0; i < pending.length; i += MAX_BATCH_SEND) {
      send(pending.slice(i, i + MAX_BATCH_SEND))
    }
  }

  function pageView(): void {
    const props: Record<string, unknown> = {}
    if (firstPageView) {
      const utm = readUtm()
      if (utm) props.utm = utm
      firstPageView = false
    }
    enqueue('page_view', props)
  }

  function pageLeave(): void {
    if (leaveSent) return
    leaveSent = true
    enqueue('page_leave', {
      seconds: Math.max(0, Math.min(7200, Math.round((Date.now() - pageStart) / 1000))),
      max_scroll: maxScroll,
    })
  }

  function resetPage(path: string): void {
    currentPath = path
    pageStart = Date.now()
    maxScroll = 0
    milestonesSent = []
    leaveSent = false
  }

  function onRouteChange(): void {
    const path = location.pathname
    if (path === currentPath) return
    pageLeave()
    resetPage(path)
    pageView()
  }

  // --- initial view ---
  pageView()

  // --- SPA route changes: history patch + popstate (rr6/rr7/Next app router) ---
  try {
    const origPush = history.pushState.bind(history)
    const origReplace = history.replaceState.bind(history)
    history.pushState = function (...args: Parameters<typeof origPush>) {
      origPush(...args)
      onRouteChange()
    }
    history.replaceState = function (...args: Parameters<typeof origReplace>) {
      origReplace(...args)
      onRouteChange()
    }
    window.addEventListener('popstate', onRouteChange)
  } catch {
    /* ignore */
  }

  // --- clicks: one delegated capture-phase listener ---
  document.addEventListener(
    'click',
    (e) => {
      try {
        const now = Date.now()
        if (now - lastClickAt < CLICK_THROTTLE_MS) return
        const target = e.target as Element | null
        const el = target?.closest?.('a, button, [role="button"]')
        if (!el) return
        lastClickAt = now
        const props: Record<string, unknown> = {
          tag: el.tagName,
          text: (el.textContent || '').trim().slice(0, 80),
        }
        const href = el.getAttribute('href')
        if (href) props.href = href.slice(0, 200)
        const testid = el.getAttribute('data-testid')
        if (testid) props.testid = testid.slice(0, 80)
        const role = el.getAttribute('role')
        if (role) props.role = role
        enqueue('ui_click', props)
      } catch {
        /* silent */
      }
    },
    true,
  )

  // --- scroll depth milestones ---
  let scrollScheduled = false
  window.addEventListener(
    'scroll',
    () => {
      if (scrollScheduled) return
      scrollScheduled = true
      setTimeout(() => {
        scrollScheduled = false
        try {
          const doc = document.documentElement
          const denom = doc.scrollHeight - window.innerHeight
          if (denom <= 0) return
          const depth = Math.min(100, Math.round((window.scrollY / denom) * 100))
          if (depth > maxScroll) maxScroll = depth
          for (const m of SCROLL_MILESTONES) {
            if (depth >= m && !milestonesSent.includes(m)) {
              milestonesSent.push(m)
              enqueue('scroll_depth', { depth: m })
            }
          }
        } catch {
          /* silent */
        }
      }, 250)
    },
    { passive: true },
  )

  // --- locked AI features (the feature-demand signal) ---
  window.addEventListener('demo-lock', (e) => {
    try {
      const detail = (e as CustomEvent).detail || {}
      const props: Record<string, unknown> = {
        feature: String(detail.feature || 'unknown').slice(0, 80),
      }
      const sample = detail.sampleUrl ?? detail.sample_url
      if (sample) props.sample_url = String(sample).slice(0, 400)
      enqueue('demo_lock_view', props)
    } catch {
      /* silent */
    }
  })

  // --- heartbeat: presence while visible ---
  setInterval(() => {
    if (document.visibilityState === 'visible') enqueue('heartbeat')
  }, HEARTBEAT_MS)

  // --- terminal flushes: dwell segment ends when the tab hides ---
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      pageLeave()
      flush()
    } else {
      // Back to the tab: a fresh dwell segment on the same path.
      pageStart = Date.now()
      leaveSent = false
    }
  })
  window.addEventListener('pagehide', () => {
    pageLeave()
    flush()
  })
}

/* --------------------------------------------------------------- helpers */

function safeGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key)
  } catch {
    return null
  }
}

function uuid4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  // RFC-4122-shaped fallback — the ingest parses this with uuid.UUID().
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const id = uuid4()
    sessionStorage.setItem(SESSION_KEY, id)
    return id
  } catch {
    // Storage blocked — a random id per page load still batches correctly.
    return uuid4()
  }
}

function captureUtm(): void {
  try {
    if (sessionStorage.getItem(UTM_KEY)) return
    const params = new URLSearchParams(location.search)
    const utm: Record<string, string> = {}
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const v = params.get(k)
      if (v) utm[k] = v.slice(0, 200)
    }
    if (Object.keys(utm).length > 0) sessionStorage.setItem(UTM_KEY, JSON.stringify(utm))
  } catch {
    /* ignore */
  }
}

function readUtm(): Record<string, string> | null {
  try {
    const raw = sessionStorage.getItem(UTM_KEY)
    return raw ? (JSON.parse(raw) as Record<string, string>) : null
  } catch {
    return null
  }
}
